import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  CardMedia,
  Fade,
  IconButton,
  Dialog,
  useMediaQuery,
} from '@mui/material';
import { ArrowBack, ArrowForward, Fullscreen, FullscreenExit, PlayArrow, Pause } from '@mui/icons-material';
import { io, Socket } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { RootState } from '../../store';
import { API_URL, SOCKET_URL } from '../../config/api';
import { rewriteToCdn } from '../../utils/cdn';
import { isResultGalleryVideo } from '../../utils/resultGalleryMedia';
import { getCollections, getCollectionById, getPublicGalleryByFolderId } from '../../services/api';
import type { PhotoGroupResponse, Collection } from '../../services/api';
import { useI18n } from '../../i18n/useI18n';
import { userCardEnterSx } from '../shared/UserPageShell';

interface Photo {
  name: string;
  url: string;
  createdAt: string;
  mediaType?: string;
}

interface PhotoGroup {
  folderId: string;
  photos: Photo[];
  createdAt: string;
  viewUrl: string;
}

interface PhotoPosition {
  x?: number | string;
  X?: number | string;
  y?: number | string;
  Y?: number | string;
  width?: number | string;
  Width?: number | string;
  height?: number | string;
  Height?: number | string;
}

interface LayoutComposeSnapshot {
  frameWidth: number;
  frameHeight: number;
  photoPositions: PhotoPosition[];
  isDuplicateMode?: boolean;
  flip?: boolean | string | number;
  Flip?: boolean | string | number;
  isFlipped?: boolean;
  IsFlipped?: boolean;
}

const SNAPSHOT_JSON_RE = /layout_video_compose_snapshot\.json$/i;

function isSnapshotJsonFile(name: string): boolean {
  return SNAPSHOT_JSON_RE.test(name || '');
}

function isUserFrameFile(name: string): boolean {
  return String(name || '').toLowerCase().startsWith('layout_compose_userframe');
}

/** File ghép layout_video đầu ra — không hiện trong live (đã có preview từ JSON). */
function isLayoutVideoOutput(name: string): boolean {
  return /^layout_video\.(mp4|mov|avi|webm)$/i.test(String(name || '').trim());
}

function readBooleanLike(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }
  return false;
}

function snapshotIsFlipped(snap: LayoutComposeSnapshot | null): boolean {
  if (!snap) return false;
  return (
    readBooleanLike(snap.flip) ||
    readBooleanLike(snap.Flip) ||
    readBooleanLike(snap.isFlipped) ||
    readBooleanLike(snap.IsFlipped)
  );
}

/** Slot ≥2: mirror khi flip; không lật 1.jpg / 1_cut* */
function shouldFlipSlotMedia(isFlipped: boolean, fileName: string, isVideo: boolean): boolean {
  if (!isFlipped) return false;
  const n = String(fileName || '').trim();
  if (/^1\.(jpe?g|png|webp)$/i.test(n)) return false;
  if (/^1_cut/i.test(n)) return false;
  const m = /^(\d+)\./i.exec(n);
  if (!m) return isVideo;
  const num = parseInt(m[1], 10);
  return num >= 2;
}

function posNum(pos: PhotoPosition, keys: (keyof PhotoPosition)[]): number {
  for (const k of keys) {
    const v = pos[k];
    if (v == null || v === '') continue;
    const num = Number(v);
    if (!Number.isNaN(num)) return num;
  }
  return NaN;
}

function buildNameKeyMap(photos: Photo[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const p of photos) {
    if (p?.name) map[String(p.name).toLowerCase()] = rewriteToCdn(p.url);
  }
  return map;
}

function resolveSlotUrl(urlByName: Record<string, string>, slot: number): string | null {
  const exts = ['mp4', 'mov', 'avi', 'webm', 'm4v', 'mkv'];
  for (const ext of exts) {
    const key = `${slot}.${ext}`.toLowerCase();
    if (urlByName[key]) return urlByName[key];
  }
  return null;
}

function resolvePoster1Url(urlByName: Record<string, string>): string | null {
  const names = ['1.jpg', '1.jpeg', '1.png', '1.webp'];
  for (const name of names) {
    const key = name.toLowerCase();
    if (urlByName[key]) return urlByName[key];
  }
  return null;
}

function resolveUserFrameOverlayUrl(urlByName: Record<string, string>): string | null {
  const keys = Object.keys(urlByName);
  const frameKey = keys.find((k) => /^layout_compose_userframe.*\.(png|webp|jpe?g)$/i.test(k));
  return frameKey ? urlByName[frameKey] : null;
}

/** Lọc file để lấy poster 1.jpg (hoặc tìm trong ảnh hợp lệ); không json / user frame / layout_video output. */
function filterLiveGalleryMedia(photos: Photo[]): Photo[] {
  return photos.filter((p) => {
    const name = String(p.name || '');
    if (p.mediaType === 'json' || /\.json$/i.test(name)) return false;
    if (isSnapshotJsonFile(name)) return false;
    if (isUserFrameFile(name)) return false;
    if (isLayoutVideoOutput(name)) return false;
    return true;
  });
}

function sortLiveMedia(a: Photo, b: Photo): number {
  const na = String(a.name || '').toLowerCase();
  const nb = String(b.name || '').toLowerCase();
  const ma = /^(\d+)\./.exec(na);
  const mb = /^(\d+)\./.exec(nb);
  if (ma && mb) return parseInt(ma[1], 10) - parseInt(mb[1], 10);
  if (ma) return -1;
  if (mb) return 1;
  return na.localeCompare(nb);
}

type LiveSlide =
  | { kind: 'layout-preview'; id: string }
  | { kind: 'media'; id: string; photo: Photo };

/** Live view: chỉ preview layout (video theo JSON) + ảnh 1.jpg — không lướt ảnh/video gốc slot khác. */
function buildSlides(photos: Photo[], hasSnapshot: boolean): LiveSlide[] {
  const media = [...filterLiveGalleryMedia(photos)].sort(sortLiveMedia);
  const slides: LiveSlide[] = [];
  if (hasSnapshot) slides.push({ kind: 'layout-preview', id: 'layout-preview' });
  const poster = media.find((p) => /^1\.(jpe?g|png|webp)$/i.test(String(p.name || '')));
  if (poster) slides.push({ kind: 'media', id: `m-${poster.name}`, photo: poster });
  return slides;
}

const LIVE_QUEUE_MAX = 10;
const COLLECTION_PAGE_SIZE = 10;

function pickCoverPhoto(photos: { name?: string; url?: string }[]): { url?: string } | undefined {
  if (!photos?.length) return undefined;
  return (
    photos.find((p) => p.name === '1.jpg') ||
    photos.find((p) => p.name === '1_cut2.jpg') ||
    photos.find((p) => p.name === '1_cut3.jpg') ||
    photos[0]
  );
}

function viewUrlForFolder(folderId: string, g?: { viewUrl?: string }): string {
  if (g?.viewUrl) return g.viewUrl;
  return `${API_URL}/results/g/${folderId}`;
}

function normalizePhotoGroup(g: PhotoGroupResponse): PhotoGroup {
  const createdAt =
    typeof g.createdAt === 'string' ? g.createdAt : new Date(g.createdAt as unknown as string).toISOString();
  return {
    folderId: g.folderId,
    createdAt,
    viewUrl: viewUrlForFolder(g.folderId, g),
    photos: (g.photos || []).map((p) => ({
      name: p.name,
      url: p.url,
      createdAt: typeof p.createdAt === 'string' ? p.createdAt : new Date((p as { createdAt?: string }).createdAt || createdAt).toISOString(),
      mediaType: p.mediaType,
    })),
  };
}

function getGalleryCoverUrl(group: PhotoGroup): string {
  const cover = pickCoverPhoto((group.photos || []).map((p) => ({ name: p.name, url: p.url })));
  return cover?.url ? rewriteToCdn(cover.url) : '/default-collection-image.jpg';
}

function groupHasSnapshotJson(group: PhotoGroup): boolean {
  return (group.photos || []).some(
    (p) => (p.mediaType === 'json' || /\.json$/i.test(String(p.name || ''))) && isSnapshotJsonFile(String(p.name || ''))
  );
}

function hasSnapshotJsonInPhotos(photos: Photo[]): boolean {
  return (photos || []).some(
    (p) => (p.mediaType === 'json' || /\.json$/i.test(String(p.name || ''))) && isSnapshotJsonFile(String(p.name || ''))
  );
}

function normalizeSocketUserId(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'object') {
    const obj = v as { _id?: unknown; $oid?: unknown; toString?: () => string };
    if (typeof obj._id === 'string') return obj._id;
    if (typeof obj.$oid === 'string') return obj.$oid;
    if (obj._id && typeof obj._id === 'object') {
      const inner = obj._id as { $oid?: unknown; toString?: () => string };
      if (typeof inner.$oid === 'string') return inner.$oid;
      const innerStr = inner.toString?.();
      if (innerStr && innerStr !== '[object Object]') return innerStr;
    }
    const s = obj.toString?.();
    if (s && s !== '[object Object]') return s;
  }
  return String(v);
}

interface LayoutPreviewStageProps {
  snapshot: LayoutComposeSnapshot;
  photos: Photo[];
}

const LayoutPreviewStage: React.FC<LayoutPreviewStageProps> = ({ snapshot, photos }) => {
  const urlByName = useMemo(() => buildNameKeyMap(photos), [photos]);
  const flip = snapshotIsFlipped(snapshot);
  const fw = Number(snapshot.frameWidth);
  const fh = Number(snapshot.frameHeight);
  const positions = snapshot.photoPositions || [];
  const isDup = !!snapshot.isDuplicateMode;
  const posterUrl = resolvePoster1Url(urlByName);
  const frameOverlayUrl = resolveUserFrameOverlayUrl(urlByName);

  if (!fw || !fh || !positions.length) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        Thiếu cấu hình layout trong snapshot.
      </Typography>
    );
  }

  /** Giống object-fit: contain — vừa khung ô slide, không kéo full màn như trước. */
  return (
    <Box
      sx={{
        position: 'relative',
        margin: 0,
        width: `min(100cqw, calc(100cqh * ${fw} / ${fh}))`,
        height: `min(100cqh, calc(100cqw * ${fh} / ${fw}))`,
        aspectRatio: `${fw} / ${fh}`,
        bgcolor: '#000',
        overflow: 'hidden',
        flex: '0 0 auto',
      }}
    >
      {posterUrl && (
        <Box
          component="img"
          src={posterUrl}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            pointerEvents: 'none',
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
      {positions.map((pos, i) => {
        const x = posNum(pos, ['x', 'X']);
        const y = posNum(pos, ['y', 'Y']);
        const w = posNum(pos, ['width', 'Width']);
        const h = posNum(pos, ['height', 'Height']);
        if (Number.isNaN(x) || Number.isNaN(y) || Number.isNaN(w) || Number.isNaN(h) || w <= 0 || h <= 0) return null;
        const slot = isDup ? 2 : i + 2;
        const url = resolveSlotUrl(urlByName, slot);
        return (
          <Box
            key={`slot-${slot}-${i}`}
            sx={{
              position: 'absolute',
              left: `${(x / fw) * 100}%`,
              top: `${(y / fh) * 100}%`,
              width: `${(w / fw) * 100}%`,
              height: `${(h / fh) * 100}%`,
              overflow: 'hidden',
              bgcolor: url ? '#f1f5f9' : '#fee2e2',
              zIndex: 1 + i,
            }}
          >
            {url ? (
              <video
                src={url}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  zIndex: 2,
                  transform: flip ? 'scaleX(-1)' : 'none',
                  transformOrigin: 'center center',
                }}
              />
            ) : (
              <Typography variant="caption" sx={{ p: 0.5, color: 'text.secondary', fontSize: 10 }}>
                Thiếu clip slot {slot}
              </Typography>
            )}
          </Box>
        );
      })}
      {frameOverlayUrl && (
        <Box
          component="img"
          src={frameOverlayUrl}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 20,
            pointerEvents: 'none',
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
    </Box>
  );
};

const LivePhotos: React.FC = () => {
  const { pages: t } = useI18n();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isLandscape = useMediaQuery('(orientation: landscape) and (max-height: 900px)');

  const [collections, setCollections] = useState<Collection[]>([]);
  const [collectionCoverFallbacks, setCollectionCoverFallbacks] = useState<Record<string, string>>({});
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [loadingMoreCollections, setLoadingMoreCollections] = useState(false);
  const [collectionsPage, setCollectionsPage] = useState(1);
  const [hasMoreCollections, setHasMoreCollections] = useState(true);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [loadingLiveQueue, setLoadingLiveQueue] = useState(false);
  const [liveQueue, setLiveQueue] = useState<PhotoGroup[]>([]);
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [collectionFolderIds, setCollectionFolderIds] = useState<Set<string>>(new Set());

  const selectedCollectionIdRef = useRef<string>('');
  const collectionFolderIdsRef = useRef<Set<string>>(new Set());
  const isRefreshingSelectedCollectionRef = useRef(false);

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSlideshowActive, setIsSlideshowActive] = useState(true);
  const [layoutSnapshot, setLayoutSnapshot] = useState<LayoutComposeSnapshot | null>(null);
  const [snapshotLoadError, setSnapshotLoadError] = useState<string | null>(null);
  const layoutSnapshotCacheRef = useRef<Record<string, LayoutComposeSnapshot>>({});

  useEffect(() => {
    selectedCollectionIdRef.current = selectedCollectionId;
  }, [selectedCollectionId]);

  useEffect(() => {
    collectionFolderIdsRef.current = collectionFolderIds;
  }, [collectionFolderIds]);

  const activeGroup = useMemo(() => {
    if (!liveQueue.length) return null;
    if (activeFolderId) {
      const found = liveQueue.find((g) => g.folderId === activeFolderId);
      if (found) return found;
    }
    return liveQueue[0];
  }, [liveQueue, activeFolderId]);

  useEffect(() => {
    if (!liveQueue.length) {
      setActiveFolderId(null);
      return;
    }
    setActiveFolderId((prev) => {
      if (prev && liveQueue.some((g) => g.folderId === prev)) return prev;
      return liveQueue[0].folderId;
    });
  }, [liveQueue]);

  const loadCollectionsPage = useCallback(
    async (page: number, append: boolean) => {
      if (!currentUser?._id) return;
      if (append) setLoadingMoreCollections(true);
      else setLoadingCollections(true);
      try {
        const res = await getCollections(page, COLLECTION_PAGE_SIZE);
        const incoming = res.data || [];
        setCollections((prev) => (append ? [...prev, ...incoming] : incoming));
        setCollectionsPage(page);
        setHasMoreCollections(page < (res.pagination?.totalPages || 1));
      } catch {
        if (!append) setCollections([]);
      } finally {
        if (append) setLoadingMoreCollections(false);
        else setLoadingCollections(false);
      }
    },
    [currentUser?._id]
  );

  useEffect(() => {
    if (!currentUser?._id) return;
    setCollections([]);
    setCollectionsPage(1);
    setHasMoreCollections(true);
    setSelectedCollectionId('');
    loadCollectionsPage(1, false);
  }, [currentUser?._id, loadCollectionsPage]);

  useEffect(() => {
    if (!collections.length) return;
    let cancelled = false;
    const candidates = collections.filter(
      (c) => !c.headerImage?.url && Array.isArray(c.galleries) && c.galleries.length > 0
    );
    if (!candidates.length) return;

    (async () => {
      const updates: Record<string, string> = {};
      await Promise.all(
        candidates.map(async (collection) => {
          try {
            const firstGalleryId = collection.galleries[0];
            if (!firstGalleryId) return;
            const gallery = await getPublicGalleryByFolderId(firstGalleryId);
            const cover = pickCoverPhoto(gallery.photos || []);
            if (cover?.url) updates[collection._id] = rewriteToCdn(cover.url);
          } catch {
            //
          }
        })
      );
      if (!cancelled && Object.keys(updates).length > 0) {
        setCollectionCoverFallbacks((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [collections]);

  const applyCollectionDetail = useCallback((detail: { galleries: PhotoGroupResponse[] }) => {
    const folderSet = new Set(detail.galleries.map((g) => g.folderId));
    setCollectionFolderIds(folderSet);
    const sorted = [...detail.galleries].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const queue = sorted.slice(0, LIVE_QUEUE_MAX).map(normalizePhotoGroup);
    setLiveQueue(queue);
    setActiveFolderId(queue[0]?.folderId ?? null);
    setCurrentSlideIndex(0);
  }, []);

  const loadCollectionById = useCallback(
    async (collectionId: string) => {
      if (!collectionId) {
        setLiveQueue([]);
        setCollectionFolderIds(new Set());
        setActiveFolderId(null);
        return;
      }
      setLoadingLiveQueue(true);
      try {
        const detail = await getCollectionById(collectionId);
        applyCollectionDetail(detail);
      } catch {
        setLiveQueue([]);
        setCollectionFolderIds(new Set());
        setActiveFolderId(null);
      } finally {
        setLoadingLiveQueue(false);
      }
    },
    [applyCollectionDetail]
  );

  useEffect(() => {
    if (!collections.length || selectedCollectionId) return;
    const firstId = collections[0]._id;
    setSelectedCollectionId(firstId);
    loadCollectionById(firstId);
  }, [collections, selectedCollectionId, loadCollectionById]);

  const handleCollectionChange = (collectionId: string) => {
    setSelectedCollectionId(collectionId);
    if (!collectionId) {
      setLiveQueue([]);
      setCollectionFolderIds(new Set());
      setActiveFolderId(null);
      return;
    }
    loadCollectionById(collectionId);
  };

  const handleCollectionListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (loadingCollections || loadingMoreCollections || !hasMoreCollections) return;
      const target = e.currentTarget;
      const nearEnd = isLandscape
        ? target.scrollTop + target.clientHeight >= target.scrollHeight - 80
        : target.scrollLeft + target.clientWidth >= target.scrollWidth - 80;
      if (!nearEnd) return;
      loadCollectionsPage(collectionsPage + 1, true);
    },
    [
      collectionsPage,
      hasMoreCollections,
      isLandscape,
      loadCollectionsPage,
      loadingCollections,
      loadingMoreCollections,
    ]
  );

  const snapshotJsonPhoto = useMemo(() => {
    if (!activeGroup?.photos?.length) return null;
    return (
      activeGroup.photos.find(
        (p) =>
          (p.mediaType === 'json' || /\.json$/i.test(p.name || '')) && isSnapshotJsonFile(p.name)
      ) ?? null
    );
  }, [activeGroup]);

  useEffect(() => {
    if (!snapshotJsonPhoto?.url) {
      setLayoutSnapshot(null);
      setSnapshotLoadError(null);
      return;
    }
    const snapshotUrl = rewriteToCdn(snapshotJsonPhoto.url);
    const cached = layoutSnapshotCacheRef.current[snapshotUrl];
    if (cached) {
      setLayoutSnapshot(cached);
      setSnapshotLoadError(null);
      return;
    }
    let cancelled = false;
    setSnapshotLoadError(null);
    fetch(snapshotUrl, { mode: 'cors', credentials: 'omit' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: LayoutComposeSnapshot) => {
        if (!cancelled) {
          layoutSnapshotCacheRef.current[snapshotUrl] = data;
          setLayoutSnapshot(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLayoutSnapshot(null);
          setSnapshotLoadError('Không tải được snapshot layout');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [snapshotJsonPhoto?.url]);

  const slides = useMemo(
    () =>
      buildSlides(
        activeGroup?.photos ?? [],
        hasSnapshotJsonInPhotos(activeGroup?.photos ?? [])
      ),
    [activeGroup?.photos]
  );

  const flipGlobal = snapshotIsFlipped(layoutSnapshot);

  const slidesRef = useRef(slides);
  const liveQueueRef = useRef(liveQueue);
  const activeFolderIdRef = useRef(activeFolderId);
  const currentSlideIndexRef = useRef(currentSlideIndex);
  const pendingSlideIndexAfterGroupChangeRef = useRef<number | null>(null);
  const galleryLoopCountRef = useRef(0);

  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);
  useEffect(() => {
    liveQueueRef.current = liveQueue;
  }, [liveQueue]);
  useEffect(() => {
    activeFolderIdRef.current = activeFolderId;
  }, [activeFolderId]);
  useEffect(() => {
    currentSlideIndexRef.current = currentSlideIndex;
  }, [currentSlideIndex]);

  /** Tự động: mỗi gallery chạy 5 vòng rồi mới sang gallery kế trong hàng đợi (lặp). */
  useEffect(() => {
    if (!isSlideshowActive) return;
    const timer = setInterval(() => {
      const s = slidesRef.current;
      const q = liveQueueRef.current;
      const af = activeFolderIdRef.current;
      const ci = currentSlideIndexRef.current;
      if (!s.length || !af || !q.length) return;

      if (ci < s.length - 1) {
        setCurrentSlideIndex(ci + 1);
        return;
      }

      const nextLoopCount = galleryLoopCountRef.current + 1;
      if (nextLoopCount < 5) {
        galleryLoopCountRef.current = nextLoopCount;
        setCurrentSlideIndex(0);
        return;
      }
      galleryLoopCountRef.current = 0;

      const gi = q.findIndex((g) => g.folderId === af);
      if (gi < 0) {
        setCurrentSlideIndex(0);
        return;
      }
      const nextG = q[(gi + 1) % q.length];
      setActiveFolderId(nextG.folderId);
      setCurrentSlideIndex(0);
    }, 5000);
    return () => clearInterval(timer);
  }, [isSlideshowActive, liveQueue.length]);

  useEffect(() => {
    const pending = pendingSlideIndexAfterGroupChangeRef.current;
    if (pending != null) {
      setCurrentSlideIndex(Math.max(0, pending));
      pendingSlideIndexAfterGroupChangeRef.current = null;
      galleryLoopCountRef.current = 0;
      return;
    }
    galleryLoopCountRef.current = 0;
    setCurrentSlideIndex(0);
  }, [activeGroup?.folderId]);

  useEffect(() => {
    const uid = currentUser?._id;
    const newSocket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      path: '/socket.io',
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      setError(null);
    });

    newSocket.on('connect_error', (err) => {
      setError(`Connection error: ${err.message}`);
      setIsConnected(false);
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('newPhotos', async (data: { userId: string; photoGroup: PhotoGroup }) => {
      const currentUid = normalizeSocketUserId(uid);
      const incomingUid = normalizeSocketUserId(data.userId);
      if (!currentUid || !incomingUid || incomingUid !== currentUid) return;
      const selectedId = selectedCollectionIdRef.current;
      if (!selectedId) return;
      const fid = data.photoGroup?.folderId;
      if (!fid) return;
      if (isRefreshingSelectedCollectionRef.current) return;

      // Yêu cầu mới: có upload mới thì reload lại top 10 của collection đang chọn.
      isRefreshingSelectedCollectionRef.current = true;
      try {
        // Có thể backend cập nhật collection hơi trễ so với socket event upload.
        const retryDelays = [0, 800, 1600];
        for (const delayMs of retryDelays) {
          if (delayMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
          const detail = await getCollectionById(selectedId);
          applyCollectionDetail(detail);
          const updatedSet = new Set(detail.galleries.map((g) => g.folderId));
          setCollectionFolderIds(updatedSet);
          if (updatedSet.has(fid)) {
            setActiveFolderId(fid);
            setCurrentSlideIndex(0);
            break;
          }
        }
      } catch {
        // Ignore transient realtime refresh errors.
      } finally {
        isRefreshingSelectedCollectionRef.current = false;
      }
    });

    setSocket(newSocket);
    return () => {
      newSocket.disconnect();
    };
  }, [applyCollectionDetail, currentUser?._id]);

  const handlePrevSlide = useCallback(() => {
    if (!activeGroup || !liveQueue.length || !slides.length) return;
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex((prev) => prev - 1);
      return;
    }
    const currentGalleryIndex = liveQueue.findIndex((g) => g.folderId === activeGroup.folderId);
    if (currentGalleryIndex < 0) return;
    const prevGallery = liveQueue[(currentGalleryIndex - 1 + liveQueue.length) % liveQueue.length];
    const prevGallerySlidesCount = buildSlides(prevGallery.photos, groupHasSnapshotJson(prevGallery)).length;
    pendingSlideIndexAfterGroupChangeRef.current = Math.max(0, prevGallerySlidesCount - 1);
    setActiveFolderId(prevGallery.folderId);
  }, [activeGroup, currentSlideIndex, liveQueue, slides.length]);

  const handleNextSlide = useCallback(() => {
    if (!activeGroup || !liveQueue.length || !slides.length) return;
    if (currentSlideIndex < slides.length - 1) {
      setCurrentSlideIndex((prev) => prev + 1);
      return;
    }
    const currentGalleryIndex = liveQueue.findIndex((g) => g.folderId === activeGroup.folderId);
    if (currentGalleryIndex < 0) return;
    const nextGallery = liveQueue[(currentGalleryIndex + 1) % liveQueue.length];
    pendingSlideIndexAfterGroupChangeRef.current = 0;
    setActiveFolderId(nextGallery.folderId);
  }, [activeGroup, currentSlideIndex, liveQueue, slides.length]);

  const toggleFullscreen = () => setIsFullscreen((v) => !v);

  const toggleSlideshow = () => {
    setIsSlideshowActive((prev) => {
      const next = !prev;
      if (!next) setCurrentSlideIndex(0);
      return next;
    });
  };

  const renderSlideContent = () => {
    if (!activeGroup || !slides.length) return null;
    const slide = slides[currentSlideIndex];
    if (!slide) return null;

    if (slide.kind === 'layout-preview') {
      if (!layoutSnapshot) {
        return <CircularProgress sx={{ color: 'white' }} />;
      }
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            minHeight: 0,
            flex: 1,
            bgcolor: '#000',
          }}
        >
          {snapshotLoadError && (
            <Typography color="error" variant="caption" sx={{ position: 'absolute', top: 8, left: 8, zIndex: 4 }}>
              {snapshotLoadError}
            </Typography>
          )}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              containerType: 'size',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LayoutPreviewStage snapshot={layoutSnapshot} photos={activeGroup.photos} />
          </Box>
        </Box>
      );
    }

    const p = slide.kind === 'media' ? slide.photo : null;
    if (!p) return null;
    const url = rewriteToCdn(p.url);
    const isVideo = isResultGalleryVideo(p);
    const flipMedia = shouldFlipSlotMedia(flipGlobal, p.name, isVideo);

    if (isVideo) {
      return (
        <video
          src={url}
          autoPlay
          muted
          loop
          playsInline
          controls
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: '#000',
            transform: flipMedia ? 'scaleX(-1)' : 'none',
            transformOrigin: 'center center',
          }}
        />
      );
    }

    return (
      <img
        src={url}
        alt={p.name}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000',
          transform: flipMedia ? 'scaleX(-1)' : 'none',
          transformOrigin: 'center center',
        }}
      />
    );
  };

  const renderLayoutPreviewContent = () => {
    if (!activeGroup || !slides.some((s) => s.kind === 'layout-preview')) return null;
    if (!layoutSnapshot) {
      return <CircularProgress sx={{ color: 'white' }} />;
    }
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          minHeight: 0,
          flex: 1,
          bgcolor: '#000',
        }}
      >
        {snapshotLoadError && (
          <Typography color="error" variant="caption" sx={{ position: 'absolute', top: 8, left: 8, zIndex: 4 }}>
            {snapshotLoadError}
          </Typography>
        )}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            containerType: 'size',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LayoutPreviewStage snapshot={layoutSnapshot} photos={activeGroup.photos} />
        </Box>
      </Box>
    );
  };

  const PhotoDisplay = () => (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, width: '100%', height: '100%' }}>
      <Fade in timeout={500}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 0,
            position: 'relative',
          }}
        >
          {activeGroup && slides.some((s) => s.kind === 'layout-preview') && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: slides[currentSlideIndex]?.kind === 'layout-preview' ? 'flex' : 'none',
              }}
            >
              {renderLayoutPreviewContent()}
            </Box>
          )}
          {slides[currentSlideIndex]?.kind !== 'layout-preview' && renderSlideContent()}
        </Box>
      </Fade>

      {activeGroup && slides.length > 1 && (
        <>
          <IconButton
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              color: 'white',
              zIndex: 8,
            }}
            onClick={handlePrevSlide}
          >
            <ArrowBack />
          </IconButton>

          <IconButton
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              color: 'white',
              zIndex: 8,
            }}
            onClick={handleNextSlide}
          >
            <ArrowForward />
          </IconButton>

          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
              maxWidth: '90%',
            }}
          >
            {slides.map((s, index) => (
              <Box
                key={s.id}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: index === currentSlideIndex ? 'primary.main' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                }}
                onClick={() => setCurrentSlideIndex(index)}
              />
            ))}
          </Box>
        </>
      )}

      {activeGroup && slides.length > 1 && (
        <IconButton
          onClick={toggleSlideshow}
          sx={{
            position: 'absolute',
            top: 8,
            right: 56,
            bgcolor: 'rgba(0,0,0,0.5)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            color: 'white',
            zIndex: 9,
          }}
        >
          {isSlideshowActive ? <Pause /> : <PlayArrow />}
        </IconButton>
      )}

      <IconButton
        onClick={toggleFullscreen}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          bgcolor: 'rgba(0,0,0,0.5)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          color: 'white',
          zIndex: 9,
        }}
      >
        {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
      </IconButton>

      {activeGroup && slides[currentSlideIndex]?.kind === 'media' && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(255,255,255,0.85)',
            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
          }}
        >
          {(slides[currentSlideIndex] as { kind: 'media'; photo: Photo }).photo.name}
        </Typography>
      )}
    </Box>
  );

  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t.livePhotos.title}
        </Typography>
        <Typography color="text.secondary">{t.livePhotos.loginRequired}</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!isConnected) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const collectionItems = loadingCollections && collections.length === 0 ? (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={isLandscape ? 80 : 118} width="100%">
      <CircularProgress size={28} />
    </Box>
  ) : collections.length === 0 ? (
    <Typography color="text.secondary" sx={{ px: 0.5, fontSize: '0.8rem' }}>
      {t.livePhotos.emptyCollections}
    </Typography>
  ) : (
    <>
      {collections.map((c, index) => {
        const selected = selectedCollectionId === c._id;
        const cover =
          c.headerImage?.url
            ? rewriteToCdn(c.headerImage.url)
            : collectionCoverFallbacks[c._id] || '/default-collection-image.jpg';
        return (
          <Box
            key={c._id}
            onClick={() => handleCollectionChange(c._id)}
            sx={{
              flex: '0 0 auto',
              width: isLandscape ? '100%' : { xs: 118, sm: 136 },
              scrollSnapAlign: 'start',
              cursor: 'pointer',
              ...userCardEnterSx(index),
            }}
          >
            <Box
              sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                border: '2px solid',
                borderColor: selected ? 'primary.main' : 'transparent',
                boxShadow: selected
                  ? (theme) => `0 8px 20px ${alpha(theme.palette.primary.main, 0.28)}`
                  : '0 4px 14px rgba(15,23,42,0.08)',
                bgcolor: '#fff',
                transform: selected && !isLandscape ? 'translateY(-2px)' : 'none',
                transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
              }}
            >
              <CardMedia
                component="img"
                image={cover}
                alt=""
                sx={{
                  height: isLandscape ? 78 : { xs: 88, sm: 100 },
                  objectFit: 'cover',
                  bgcolor: '#f8fafc',
                  display: 'block',
                }}
              />
              <Typography
                noWrap
                title={c.title}
                sx={{
                  px: 0.7,
                  py: 0.55,
                  fontSize: isLandscape ? '0.68rem' : '0.78rem',
                  fontWeight: selected ? 800 : 650,
                  color: selected ? 'primary.main' : 'text.primary',
                  textAlign: 'center',
                }}
              >
                {c.title}
              </Typography>
            </Box>
          </Box>
        );
      })}
      {loadingMoreCollections && (
        <Box display="flex" alignItems="center" justifyContent="center" py={0.75} flexShrink={0}>
          <CircularProgress size={20} />
        </Box>
      )}
    </>
  );

  const galleryItems =
    liveQueue.length > 0 && activeGroup
      ? liveQueue.map((g, index) => {
          const selected = g.folderId === activeGroup.folderId;
          return (
            <Box
              key={g.folderId}
              onClick={() => {
                setActiveFolderId(g.folderId);
                setCurrentSlideIndex(0);
              }}
              sx={{
                flex: '0 0 auto',
                width: isLandscape ? '100%' : { xs: 92, sm: 108 },
                scrollSnapAlign: 'start',
                cursor: 'pointer',
                ...userCardEnterSx(index),
              }}
            >
              <Box
                sx={{
                  borderRadius: 2.25,
                  overflow: 'hidden',
                  border: '2px solid',
                  borderColor: selected ? 'primary.main' : 'transparent',
                  boxShadow: selected
                    ? (theme) => `0 6px 16px ${alpha(theme.palette.primary.main, 0.24)}`
                    : '0 3px 10px rgba(15,23,42,0.08)',
                  bgcolor: '#fff',
                }}
              >
                <CardMedia
                  component="img"
                  image={getGalleryCoverUrl(g)}
                  alt=""
                  sx={{
                    height: isLandscape ? 86 : { xs: 92, sm: 108 },
                    objectFit: 'cover',
                    bgcolor: '#f8fafc',
                    display: 'block',
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                display="block"
                textAlign="center"
                sx={{
                  mt: 0.4,
                  fontWeight: selected ? 800 : 600,
                  color: selected ? 'primary.main' : 'text.secondary',
                }}
              >
                {index + 1}
              </Typography>
            </Box>
          );
        })
      : null;

  const liveStage = loadingLiveQueue ? (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%" minHeight={isLandscape ? 0 : 280}>
      <CircularProgress />
    </Box>
  ) : !activeGroup ? (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100%"
      minHeight={isLandscape ? 0 : 220}
      sx={{
        borderRadius: 3,
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor: alpha('#ff8ba7', 0.04),
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {t.livePhotos.emptyGallery}
      </Typography>
    </Box>
  ) : (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: isLandscape ? '100%' : { xs: 400, sm: 500, md: 560 },
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: '#0b0b0f',
        boxShadow: '0 16px 40px rgba(15,23,42,0.18)',
        display: isFullscreen ? 'none' : 'block',
      }}
    >
      <PhotoDisplay />
    </Box>
  );

  const fullscreenDialog = (
    <Dialog
      fullScreen
      open={isFullscreen}
      onClose={toggleFullscreen}
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'black',
          display: 'flex',
          flexDirection: 'column',
          m: 0,
        },
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', height: '100%' }}>
        <PhotoDisplay />
      </Box>
    </Dialog>
  );

  const scrollerSx: SxProps<Theme> = isLandscape
    ? {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        pr: 0.25,
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: alpha('#ff8ba7', 0.45),
          borderRadius: 99,
        },
      }
    : {
        display: 'flex',
        gap: 1.25,
        overflowX: 'auto',
        pb: 0.75,
        mx: { xs: -0.5, sm: 0 },
        px: { xs: 0.5, sm: 0.25 },
        scrollSnapType: 'x mandatory',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: alpha('#ff8ba7', 0.45),
          borderRadius: 99,
        },
      };

  if (isLandscape) {
    const columnHeaderSx: SxProps<Theme> = {
      flexShrink: 0,
      fontWeight: 800,
      fontSize: { xs: '0.68rem', sm: '0.75rem' },
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
      color: 'text.secondary',
      textAlign: 'center',
      px: 0.25,
      py: 0.6,
      mb: 0.5,
      bgcolor: 'background.default',
      position: 'sticky',
      top: 0,
      zIndex: 2,
    };

    return (
      <Box
        sx={{
          height: { xs: 'calc(100dvh - 56px)', sm: 'calc(100dvh - 64px)' },
          display: 'flex',
          gap: 1,
          px: 1,
          py: 1,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: { xs: 96, sm: 112 },
            flexShrink: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography sx={columnHeaderSx}>{t.livePhotos.galleryListTitle}</Typography>
          <Box sx={[scrollerSx, { flex: 1, minHeight: 0 }]}>{galleryItems}</Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>{liveStage}</Box>
        <Box
          sx={{
            width: { xs: 118, sm: 136 },
            flexShrink: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography sx={columnHeaderSx}>{t.livePhotos.collectionListTitle}</Typography>
          <Box onScroll={handleCollectionListScroll} sx={[scrollerSx, { flex: 1, minHeight: 0 }]}>
            {collectionItems}
          </Box>
        </Box>
        {fullscreenDialog}
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" className="user-page-enter" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
      <Typography
        fontWeight={800}
        sx={{
          fontSize: { xs: '1.35rem', sm: '2rem' },
          letterSpacing: '-0.03em',
          mb: { xs: 1.25, sm: 1.75 },
        }}
      >
        {t.livePhotos.title}
      </Typography>

      <Typography
        fontWeight={800}
        sx={{
          fontSize: { xs: '0.82rem', sm: '0.9rem' },
          color: 'text.secondary',
          mb: 1,
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: 3,
          py: 0.6,
          bgcolor: 'background.default',
        }}
      >
        {t.livePhotos.collectionListTitle}
      </Typography>
      <Box onScroll={handleCollectionListScroll} sx={[scrollerSx, { mb: 2 }]}>
        {collectionItems}
      </Box>

      <Box sx={{ mb: 2 }}>{liveStage}</Box>

      {galleryItems && (
        <Box>
          <Typography
            fontWeight={800}
            sx={{
              fontSize: { xs: '0.82rem', sm: '0.9rem' },
              color: 'text.secondary',
              mb: 1,
              position: 'sticky',
              top: { xs: 56, sm: 64 },
              zIndex: 3,
              py: 0.6,
              bgcolor: 'background.default',
            }}
          >
            {t.livePhotos.galleryListTitle}
          </Typography>
          <Box sx={scrollerSx}>{galleryItems}</Box>
        </Box>
      )}

      {fullscreenDialog}
    </Container>
  );
};

export default LivePhotos;
